import React, { useState } from 'react'

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

function calcEmi(amt, rate, n) {
  const r = rate / 100 / 12
  return r === 0 ? amt / n : amt * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1)
}

function loanStats(l) {
  const balance = parseFloat(l.balance)
  const emi = parseFloat(l.emi_amount)
  const r = l.interest_rate / 100 / 12
  const n = (r > 0 && emi > r * balance)
    ? Math.ceil(Math.log(emi / (emi - r * balance)) / Math.log(1 + r))
    : Math.ceil(balance / (emi || 1))
  const totalPaid = emi * n
  return {
    emi, balance, rate: l.interest_rate,
    months: Math.round(n),
    interest: Math.max(0, totalPaid - balance),
    ppct: Math.round((1 - balance / parseFloat(l.original_amount)) * 100)
  }
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px',
  padding: '0.55rem 0.75rem', fontSize: '0.88rem', color: 'var(--text)',
  width: '100%', boxSizing: 'border-box', outline: 'none'
}
const labelStyle = {
  display: 'block', fontFamily: 'monospace', fontSize: '0.67rem',
  fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

function CompareCards({ rowsData, nameA, nameB }) {
  if (!rowsData) return null
  const { rows, verdict } = rowsData
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.8rem' }}>
        {[{ name: nameA, isA: true }, { name: nameB, isA: false }].map(({ name, isA }) => (
          <div key={name} style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{
              padding: '0.65rem 1rem',
              fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '500',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              background: isA ? 'rgba(61,74,92,0.1)' : 'rgba(201,168,76,0.1)',
              color: isA ? '#3d4a5c' : '#7a5d1e'
            }}>
              ▌ {name}
            </div>
            {rows.map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 1rem', borderTop: '1px solid var(--border)', fontSize: '0.78rem' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>{r.label}</span>
                <span style={{
                  fontFamily: 'monospace', fontWeight: '500',
                  color: (isA ? r.winA : !r.winA) ? '#4a6741' : '#c0392b'
                }}>
                  {isA ? r.a : r.b}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ padding: '0.85rem 1rem', background: 'rgba(74,103,65,0.08)', borderRadius: '6px', borderLeft: '3px solid #4a6741', fontSize: '0.8rem', lineHeight: 1.6 }}>
        🔍 <strong>Verdict:</strong> {verdict}
      </div>
    </div>
  )
}

export default function LoanComparison({ loans }) {
  const [loanA, setLoanA] = useState('')
  const [loanB, setLoanB] = useState('')
  const [calc, setCalc] = useState({ aAmt: '', aRate: '', aTerm: '', bAmt: '', bRate: '', bTerm: '' })

  function getCompareData() {
    if (!loanA || !loanB || loanA === loanB) return null
    const la = loans.find(l => l.id === loanA)
    const lb = loans.find(l => l.id === loanB)
    if (!la || !lb) return null
    const sA = loanStats(la), sB = loanStats(lb)
    const rows = [
      { label: 'Current Balance', a: fmt(sA.balance), b: fmt(sB.balance), winA: sA.balance < sB.balance },
      { label: 'Interest Rate', a: sA.rate + '%', b: sB.rate + '%', winA: sA.rate < sB.rate },
      { label: 'Monthly EMI', a: fmt(sA.emi), b: fmt(sB.emi), winA: sA.emi < sB.emi },
      { label: 'Months Remaining', a: sA.months + ' mo', b: sB.months + ' mo', winA: sA.months < sB.months },
      { label: 'Est. Total Interest', a: fmt(sA.interest), b: fmt(sB.interest), winA: sA.interest < sB.interest },
      { label: '% Paid Off', a: sA.ppct + '%', b: sB.ppct + '%', winA: sA.ppct > sB.ppct },
    ]
    const wA = rows.filter(r => r.winA).length
    const winner = wA > rows.length - wA ? la.name : lb.name
    const wscore = Math.max(wA, rows.length - wA)
    return { rows, verdict: `${winner} is in better shape on ${wscore} out of ${rows.length} metrics.`, nameA: la.name, nameB: lb.name }
  }

  function getCalcData() {
    const { aAmt, aRate, aTerm, bAmt, bRate, bTerm } = calc
    if ([aAmt, aRate, aTerm, bAmt, bRate, bTerm].some(v => !v || isNaN(v))) return null
    const eA = calcEmi(+aAmt, +aRate, +aTerm), eB = calcEmi(+bAmt, +bRate, +bTerm)
    const tA = eA * +aTerm, tB = eB * +bTerm
    const iA = tA - +aAmt, iB = tB - +bAmt
    const rows = [
      { label: 'Monthly EMI', a: fmt(eA), b: fmt(eB), winA: eA < eB },
      { label: 'Total Payment', a: fmt(tA), b: fmt(tB), winA: tA < tB },
      { label: 'Total Interest', a: fmt(iA), b: fmt(iB), winA: iA < iB },
      { label: 'Tenure', a: aTerm + ' mo', b: bTerm + ' mo', winA: +aTerm < +bTerm },
    ]
    const saving = Math.abs(iA - iB)
    const cheaper = iA < iB ? 'Option A' : 'Option B'
    return { rows, verdict: `${cheaper} saves you ${fmt(saving)} in total interest. Choose it if the EMI fits your budget.`, nameA: 'Option A', nameB: 'Option B' }
  }

  const cmpData = getCompareData()
  const calcData = getCalcData()

  const panelStyle = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: '8px', overflow: 'hidden'
  }
  const panelHdrStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)'
  }

  return (
    <div>
      <div style={{ marginBottom: '1.4rem' }}>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0 }}>Loan Comparison</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Compare loans side by side to make smarter financial decisions.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Compare existing loans */}
        <div style={panelStyle}>
          <div style={panelHdrStyle}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Compare Your Loans</span>
          </div>
          <div style={{ padding: '1.2rem 1.4rem', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
              <div>
                <label style={labelStyle}>Loan A</label>
                <select style={inputStyle} value={loanA} onChange={e => setLoanA(e.target.value)}>
                  <option value="">-- Select --</option>
                  {loans.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Loan B</label>
                <select style={inputStyle} value={loanB} onChange={e => setLoanB(e.target.value)}>
                  <option value="">-- Select --</option>
                  {loans.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{ padding: '1.2rem 1.4rem' }}>
            {cmpData
              ? <CompareCards rowsData={cmpData} nameA={cmpData.nameA} nameB={cmpData.nameB} />
              : <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Select two different loans above to compare.</div>
            }
          </div>
        </div>

        {/* Custom calculator */}
        <div style={panelStyle}>
          <div style={panelHdrStyle}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Custom Calculator</span>
          </div>
          <div style={{ padding: '1.2rem 1.4rem' }}>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>Compare any two loan offers from different banks.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.6rem' }}>
              {['Option A', 'Option B'].map((o, i) => (
                <div key={o} style={{
                  fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase',
                  padding: '0.35rem 0.6rem', borderRadius: '4px',
                  background: i === 0 ? 'rgba(61,74,92,0.1)' : 'rgba(201,168,76,0.1)',
                  color: i === 0 ? '#3d4a5c' : '#7a5d1e'
                }}>{o}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              {[
                { label: 'Amount (₹)', keyA: 'aAmt', keyB: 'bAmt', placeholder: '500000' },
                { label: 'Rate % p.a.', keyA: 'aRate', keyB: 'bRate', placeholder: '8.5' },
                { label: 'Tenure (months)', keyA: 'aTerm', keyB: 'bTerm', placeholder: '240' },
              ].map(row => (
                <React.Fragment key={row.label}>
                  <div>
                    <label style={labelStyle}>{row.label}</label>
                    <input style={inputStyle} type="text" inputMode="decimal" placeholder={row.placeholder}
                      value={calc[row.keyA]} onChange={e => setCalc(p => ({ ...p, [row.keyA]: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>{row.label}</label>
                    <input style={inputStyle} type="text" inputMode="decimal" placeholder={row.placeholder}
                      value={calc[row.keyB]} onChange={e => setCalc(p => ({ ...p, [row.keyB]: e.target.value }))} />
                  </div>
                </React.Fragment>
              ))}
            </div>
            {calcData && (
              <div style={{ marginTop: '1rem' }}>
                <CompareCards rowsData={calcData} nameA="Option A" nameB="Option B" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}